import { useState, useEffect } from "react";
import {
  HiOutlineAcademicCap,
  HiOutlinePlay,
  HiOutlineDocumentText,
  HiOutlineClipboardDocumentCheck,
  HiOutlineQuestionMarkCircle,
  HiOutlineArrowDownTray,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineArrowLeft,
  HiOutlineLockClosed,
  HiOutlineCheckCircle,
  HiOutlineArrowPath,
} from "react-icons/hi2";
import PortalLayout from "./PortalLayout";
import { portalAPI } from "./portalApi";
import "./CoursesPage.css";
import Picture from "../components/utils/Picture";

/* ── Convert any YouTube URL format to a proper embed URL ── */
const toEmbedUrl = (url) => {
  if (!url) return "";
  try {
    // Already an embed URL
    if (url.includes("youtube.com/embed/") || url.includes("player.vimeo.com")) return url;

    // youtu.be/VIDEO_ID
    const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/);
    if (shortMatch) return `https://www.youtube-nocookie.com/embed/${shortMatch[1]}`;

    // youtube.com/watch?v=VIDEO_ID
    const watchMatch = url.match(/[?&]v=([a-zA-Z0-9_-]{11})/);
    if (watchMatch) return `https://www.youtube-nocookie.com/embed/${watchMatch[1]}`;

    // youtube.com/shorts/VIDEO_ID
    const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/);
    if (shortsMatch) return `https://www.youtube-nocookie.com/embed/${shortsMatch[1]}`;

    // Vimeo: vimeo.com/VIDEO_ID
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;

    return url; // return as-is for direct MP4 or unknown formats
  } catch (_) {
    return url;
  }
};

const TYPE_ICON = {
  video: HiOutlinePlay,
  text: HiOutlineDocumentText,
  task: HiOutlineClipboardDocumentCheck,
  quiz: HiOutlineQuestionMarkCircle,
  download: HiOutlineArrowDownTray,
};
const TYPE_LABEL = { video: "Video", text: "Reading", task: "Task", quiz: "Quiz", download: "Download" };

const opensLabel = (d) => new Date(`${d}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric" });
const fmt = (mins) => (mins ? (mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} min`) : "");

/* ── Quiz renderer (reads JSON stored in content) ── */
function QuizViewer({ content }) {
  const [answers, setAnswers] = useState({});
  const [checked, setChecked] = useState(false);

  let questions = [];
  try {
    questions = JSON.parse(content || "[]");
  } catch (_) {}

  if (!questions.length) return <p className="cp__no-content">No quiz questions yet.</p>;

  const handleCheck = () => setChecked(true);

  return (
    <div className="cp__quiz">
      {questions.map((q, qi) => (
        <div key={q.id || qi} className="cp__quiz-q">
          <p className="cp__quiz-q-num">Question {qi + 1}</p>
          <p className="cp__quiz-q-label">{q.question}</p>
          {q.type === "multiple" ? (
            <div className="cp__quiz-options">
              {(q.options || []).map((opt, oi) => {
                const selected = answers[q.id] === opt.id;
                const isCorrect = q.correctId === opt.id;
                const showResult = checked && (selected || isCorrect);
                return (
                  <label
                    key={opt.id || oi}
                    className={`cp__quiz-opt ${selected ? "cp__quiz-opt--selected" : ""} ${checked && isCorrect ? "cp__quiz-opt--correct" : ""} ${checked && selected && !isCorrect ? "cp__quiz-opt--wrong" : ""}`}
                  >
                    <input type="radio" name={`q-${q.id}`} checked={selected} disabled={checked} onChange={() => setAnswers((a) => ({ ...a, [q.id]: opt.id }))} />
                    <span className="cp__quiz-opt-letter">{String.fromCharCode(65 + oi)}</span>
                    <span className="cp__quiz-opt-text">{opt.text}</span>
                    {showResult && isCorrect && <HiOutlineCheckCircle className="cp__quiz-correct-icon" />}
                  </label>
                );
              })}
            </div>
          ) : (
            <textarea
              className="cp__quiz-text-answer"
              rows={3}
              placeholder="Write your answer here…"
              disabled={checked}
              value={answers[q.id] || ""}
              onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
            />
          )}
        </div>
      ))}
      <div className="cp__quiz-actions">
        {!checked ? (
          <button type="button" className="cp__quiz-submit" onClick={handleCheck} disabled={questions.some((q) => q.type === "multiple" && !answers[q.id])}>
            Submit Answers
          </button>
        ) : (
          <div className="cp__quiz-result">
            <HiOutlineCheckCircle /> Answers submitted
          </div>
        )}
        {checked && (
          <button
            type="button"
            className="cp__quiz-retry"
            onClick={() => {
              setAnswers({});
              setChecked(false);
            }}
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

/* ── Lesson viewer panel ── */
function LessonViewer({ lesson, onClose, onToggleDone, saving }) {
  const Icon = TYPE_ICON[lesson.type] || HiOutlineDocumentText;

  const renderContent = () => {
    if (lesson.type === "video") {
      const raw = lesson.video_url || "";
      const isYT = raw.includes("youtube") || raw.includes("youtu.be");
      const isVimeo = raw.includes("vimeo");
      if (isYT || isVimeo) {
        const embedSrc = toEmbedUrl(raw);
        return (
          <div className="cp__video-wrap">
            <iframe
              src={embedSrc}
              title={lesson.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              frameBorder="0"
              referrerPolicy="strict-origin-when-cross-origin"
            />
          </div>
        );
      }
      if (raw) return <video controls className="cp__video-native" src={raw} />;
      return <p className="cp__no-content">Video URL not configured yet.</p>;
    }

    if (lesson.type === "quiz") return <QuizViewer content={lesson.content} />;

    if (lesson.type === "download") {
      return (
        <div className="cp__download-block">
          <HiOutlineArrowDownTray className="cp__dl-icon" />
          <p className="cp__dl-title">Downloadable Resource</p>
          {lesson.content && <p className="cp__dl-desc">{lesson.content}</p>}
          {lesson.video_url && (
            <a href={lesson.video_url} target="_blank" rel="noreferrer" className="cp__dl-btn">
              <HiOutlineArrowDownTray /> Download File
            </a>
          )}
        </div>
      );
    }

    if (lesson.content) {
      return <div className="cp__text-content">{lesson.content.split("\n").map((l, i) => (l.trim() ? <p key={i}>{l}</p> : <br key={i} />))}</div>;
    }

    return <p className="cp__no-content">Content for this lesson is coming soon.</p>;
  };

  return (
    <div className="cp__lesson-panel">
      <div className="cp__lesson-panel-head">
        <button type="button" className="cp__lesson-back" onClick={onClose}>
          <HiOutlineArrowLeft /> Back to Course
        </button>
        <div className="cp__lesson-type-badge">
          <Icon /> {TYPE_LABEL[lesson.type] || lesson.type}
          {lesson.duration_min && <span className="cp__lesson-dur">{fmt(lesson.duration_min)}</span>}
        </div>
      </div>
      <div className="cp__lesson-panel-body">
        <h2 className="cp__lesson-title">{lesson.title}</h2>
        {renderContent()}
        {/* Description / transcript for video */}
        {lesson.type === "video" && lesson.content && (
          <div className="cp__lesson-transcript">
            <p className="cp__transcript-label">Description / Transcript</p>
            <p className="cp__transcript-text">{lesson.content}</p>
          </div>
        )}
        <div className="cp__lesson-done-row">
          <button type="button" className={`cp__done-btn ${lesson.done ? "is-done" : ""}`} aria-pressed={!!lesson.done} disabled={saving} onClick={() => onToggleDone(lesson)}>
            <HiOutlineCheckCircle /> {lesson.done ? "Completed" : "Mark as complete"}
          </button>
          {lesson.done && <span className="cp__done-hint">Click again to mark it not done.</span>}
        </div>
      </div>
    </div>
  );
}

/* ── Course viewer (chapter accordion + lesson list) ── */
function CourseViewer({ courseId, onBack, onProgress }) {
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openChapters, setOpenChapters] = useState({});
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    portalAPI
      .course(courseId)
      .then((res) => {
        const c = res?.data;
        setCourse(c);
        // Open first chapter by default
        if (c?.chapters?.length) setOpenChapters({ [c.chapters[0].id]: true });
      })
      .catch((err) => setError(err.message || "Could not load course."))
      .finally(() => setLoading(false));
  }, [courseId]);

  const toggleChapter = (id) => setOpenChapters((p) => ({ ...p, [id]: !p[id] }));

  const totalLessons = course?.chapters?.reduce((s, c) => s + (c.lessons?.length || 0), 0) || 0;

  if (loading)
    return (
      <div className="cp__loading">
        <div className="cp__spinner" />
        <p>Loading course…</p>
      </div>
    );
  if (error)
    return (
      <div className="cp__error-state">
        <p>{error}</p>
        <button type="button" className="p-btn p-btn--outline" onClick={onBack}>
          <HiOutlineArrowLeft /> Back
        </button>
      </div>
    );

  const lessonById = (id) => course.chapters.flatMap((ch) => ch.lessons || []).find((l) => l.id === id);

  const toggleDone = async (lesson) => {
    setSaving(true);
    try {
      const res = await portalAPI.lessonDone(courseId, lesson.id, !lesson.done);
      setCourse((c) => ({
        ...c,
        progress: res.data.progress,
        chapters: c.chapters.map((ch) => ({ ...ch, lessons: ch.lessons.map((l) => (l.id === lesson.id ? { ...l, done: !lesson.done } : l)) })),
      }));
      onProgress?.(courseId, res.data.progress);
    } catch (_) { /* the button simply stays as it was */ }
    finally { setSaving(false); }
  };

  const activeLesson = activeLessonId ? lessonById(activeLessonId) : null;
  if (activeLesson) {
    return <LessonViewer lesson={activeLesson} saving={saving} onToggleDone={toggleDone} onClose={() => setActiveLessonId(null)} />;
  }

  return (
    <div className="cp__course-viewer">
      <div className="cp__viewer-topbar">
        <button type="button" className="cp__back-btn" onClick={onBack}>
          <HiOutlineArrowLeft /> All Courses
        </button>
      </div>

      {/* Hero banner */}
      {course.thumbnail ? (
        <div className="cp__course-hero">
          <Picture src={course.thumbnail} alt={course.title} priority sizes="100vw" width={1600} height={600} onError={(e) => (e.target.style.display = "none")} />
          <div className="cp__course-hero-overlay" />
          <div className="cp__course-hero-text">
            <h1 className="cp__course-hero-title">{course.title}</h1>
            <div className="cp__course-hero-meta">
              <span>{course.chapters?.length || 0} chapters</span>
              <span>·</span>
              <span>{totalLessons} lessons</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="cp__course-header-plain">
          <h1 className="cp__course-title">{course.title}</h1>
          {course.description && <p className="cp__course-desc">{course.description}</p>}
          <div className="cp__course-meta">
            <span>{course.chapters?.length || 0} chapters</span>
            <span>·</span>
            <span>{totalLessons} lessons</span>
          </div>
        </div>
      )}

      {course.progress?.total > 0 && (
        <div className="cp__progress" aria-label="Your progress">
          <div className="cp__progress-bar"><span style={{ width: `${(100 * course.progress.done) / course.progress.total}%` }} /></div>
          <p className="cp__progress-text">{course.progress.complete ? "You've completed this course." : `${course.progress.done} of ${course.progress.total} lessons done`}</p>
        </div>
      )}

      {/* Chapter accordion */}
      <div className="cp__chapters">
        {(course.chapters || []).map((ch, ci) => (
          <div key={ch.id} className="cp__chapter">
            <button type="button" className="cp__chapter-head" onClick={() => toggleChapter(ch.id)}>
              <div className="cp__chapter-num-badge">{ci + 1}</div>
              <div className="cp__chapter-title-group">
                <p className="cp__chapter-title">{ch.title}</p>
                <p className="cp__chapter-subtitle">
                  {ch.lessons?.length || 0} lesson{ch.lessons?.length !== 1 ? "s" : ""}
                </p>
              </div>
              <span className="cp__chapter-toggle">{openChapters[ch.id] ? <HiOutlineChevronDown /> : <HiOutlineChevronRight />}</span>
            </button>
            {openChapters[ch.id] && (
              <div className="cp__lessons">
                {ch.description && <p className="cp__chapter-desc">{ch.description}</p>}
                {(ch.lessons || []).length === 0 ? (
                  <p className="cp__lessons-empty">No lessons in this chapter yet.</p>
                ) : (
                  (ch.lessons || []).map((l, li) => {
                    return (
                      <button key={l.id} type="button" className={`cp__lesson-row ${l.done ? "cp__lesson-row--done" : ""}`} onClick={() => setActiveLessonId(l.id)}>
                        <div className="cp__lesson-num-badge">{l.done ? <HiOutlineCheckCircle aria-label="Done" /> : li + 1}</div>
                        <div className={`cp__lesson-type-dot cp__lesson-type-dot--${l.type}`} />
                        <div className="cp__lesson-info">
                          <p className="cp__lesson-name">{l.title}</p>
                          <div className="cp__lesson-tags">
                            <span className="cp__lesson-tag">{TYPE_LABEL[l.type]}</span>
                            {l.duration_min && <span className="cp__lesson-dur-tag">{fmt(l.duration_min)}</span>}
                            {!l.is_required && <span className="cp__lesson-tag">Optional</span>}
                          </div>
                        </div>
                        <HiOutlineChevronRight className="cp__lesson-arrow" />
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Main CoursesPage ── */
export default function CoursesPage() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeCourse, setActiveCourse] = useState(null);

  useEffect(() => {
    portalAPI
      .courses()
      .then((res) => setCourses(res?.data || []))
      .catch(() => setError("Could not load courses."))
      .finally(() => setLoading(false));
  }, []);

  // If viewing a specific course, don't wrap in PortalLayout header
  if (activeCourse) {
    return (
      <PortalLayout eyebrow="Learning" pageTitle={activeCourse.title} quote="Knowledge is power." quoteAuthor="Francis Bacon">
        <CourseViewer courseId={activeCourse.id} onBack={() => setActiveCourse(null)}
          onProgress={(id, progress) => setCourses((list) => list.map((c) => (c.id === id ? { ...c, progress } : c)))} />
      </PortalLayout>
    );
  }

  return (
    <PortalLayout eyebrow="Learning" pageTitle="Courses & Resources" quote="An investment in knowledge pays the best interest." quoteAuthor="Benjamin Franklin">
      <p className="cp__sub">Learning resources prepared by the Forge team to help you get the most from your journey.</p>

      {loading ? (
        <div className="cp__loading">
          <div className="cp__spinner" />
          <p>Loading courses…</p>
        </div>
      ) : error ? (
        <div className="pc cp__error-state">
          <HiOutlineArrowPath /> {error}
          <button
            type="button"
            className="p-btn p-btn--outline"
            onClick={() => {
              setLoading(true);
              setError("");
              portalAPI
                .courses()
                .then((res) => setCourses(res?.data || []))
                .catch(() => setError("Could not load."))
                .finally(() => setLoading(false));
            }}
          >
            Try again
          </button>
        </div>
      ) : courses.length === 0 ? (
        <div className="pc cp__empty">
          <HiOutlineAcademicCap className="cp__empty-icon" />
          <p className="cp__empty-title">No courses available yet</p>
          <p className="cp__empty-sub">The Forge team is preparing learning materials for you. Check back soon.</p>
        </div>
      ) : (
        <div className="cp__course-grid">
          {courses.map((course) => (
            <button key={course.id} type="button" className={`cp__course-card ${course.open === false ? "cp__course-card--locked" : ""}`}
              disabled={course.open === false} onClick={() => setActiveCourse(course)}>
              <div className="cp__card-thumb">
                {course.thumbnail ? (
                  <Picture src={course.thumbnail} alt="" sizes="(min-width: 900px) 320px, 100vw" width={400} height={225} onError={(e) => (e.target.style.display = "none")} />
                ) : (
                  <div className="cp__card-thumb-placeholder">
                    <HiOutlineAcademicCap />
                  </div>
                )}
              </div>
              <div className="cp__card-body">
                {course.open === false && (
                  <div className="cp__card-access">
                    <HiOutlineLockClosed /> {course.opensAt ? `Opens ${opensLabel(course.opensAt)}` : "Not open yet"}
                  </div>
                )}
                <h3 className="cp__card-title">{course.title}</h3>
                {course.description && <p className="cp__card-desc">{course.description}</p>}
                <div className="cp__card-footer">
                  <div className="cp__card-meta">
                    <span>{course.chapter_count || 0} ch.</span>
                    <span>·</span>
                    <span>{course.lesson_count || 0} lessons</span>
                  </div>
                  <span className="cp__card-cta">
                    {course.open === false ? "" : course.progress?.complete ? "Completed" : course.progress?.done ? `${course.progress.done}/${course.progress.total} · Continue` : "Start"}
                  </span>
                </div>
                {course.progress?.total > 0 && course.progress.done > 0 && (
                  <div className="cp__card-progress" aria-hidden="true"><span style={{ width: `${(100 * course.progress.done) / course.progress.total}%` }} /></div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </PortalLayout>
  );
}
